
import {
	InitialSetupArguments,
	LoginArguments,
	TwoFactorConfiguration,
	TwoFactorMethods,
} from 'Services/TwoFactor/Common';
import { FormEventHandler } from 'react';
import { useLocalStore } from 'mobx-react';
import { validate2faCode } from 'Services/Api/AuthorizationService';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { Button } from 'Views/Components/Button/Button';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import alertToast from 'Util/ToastifyUtils';
import useAsync from 'Hooks/useAsync';
import Spinner from 'Views/Components/Spinner/Spinner';
import If from 'Views/Components/If/If';
import { ButtonGroup } from 'Views/Components/Button/ButtonGroup';
import { logout } from 'Util/NavigationUtils';
import { useLocation } from 'react-router-dom';
import { Heading } from 'Views/Components/Heading/Heading';

export const EmailTokenType = 'Email';

export interface EmailInitialSetupProps extends InitialSetupArguments {
}

export function EmailInitialSetup({
	close,
	configurePromise,
	loggedInUserName,
	userName,
}: EmailInitialSetupProps) {

	const location = useLocation();
	const response = useAsync(() => configurePromise, []);
	const inputData = useLocalStore(() => ({
		code: '',
	}));

	const onSubmit: FormEventHandler = async e => {
		e.preventDefault();
		try {
			await validate2faCode({
				token: inputData.code,
				method: EmailTokenType,
			});
			alertToast('Successfully enabled two factor authentication', 'success');
			close();
		} catch (error) {
			console.error(error);
			alertToast('Invalid two factor code', 'error');
		}
	};

	const onCloseAndLogout = () => logout(location.pathname);

	const onClose = () => close();

	if (response.type === 'loading') return <Spinner />;

	if (response.type === 'error') {
		console.error(response.error);
		return (
			<div>
				<p>There was an error configuring email authentication.</p>
				<Button onClick={() => close(true)}>Close</Button>
			</div>
		);
	}

	return (
		<div>
			<Heading tag="h4">Email Authentication</Heading>

			{userName === loggedInUserName && (
				<>
					<p>
						You now have two factor authentication configured for your account. To finish the setup process,
						you will have been sent an email with a code. Enter the code in the confirmation box below.
					</p>
					<p>
						If you do not enter the code you will be logged out from the application.
					</p>
					<form onSubmit={onSubmit}>
						<TextField
							model={inputData}
							modelProperty="code"
							label="Authenticator Code"
						/>
						<ButtonGroup>
							<Button type="submit">Submit</Button>
							<Button onClick={onCloseAndLogout}>Close and logout</Button>
						</ButtonGroup>
					</form>
				</>
			)}

			<If condition={userName !== loggedInUserName}>
				<p>The user {userName} now has two factor authentication enabled for their account.</p>
				<Button onClick={onClose}>Close</Button>
			</If>
		</div>
	);

}

export interface EmailTwoFactorPageProps extends LoginArguments {

}

export function EmailTwoFactorPage({
	onTwoFactorSuccess,
	rememberMe,
}: EmailTwoFactorPageProps) {

	const inputData = useLocalStore(() => ({
		code: '',
		rememberTwoFactor: false,
	}));

	const onSubmit: FormEventHandler = async e => {
		e.preventDefault();
		try {
			await validate2faCode({
				method: EmailTokenType,
				token: inputData.code,
				rememberTwoFactor: inputData.rememberTwoFactor,
				rememberMe: rememberMe,
			});
			onTwoFactorSuccess?.();
		} catch (error) {
			alertToast(
				`Two factor error ${error}`,
				'error',
			);
		}
	};

	return (
		<div className="body-content">
			<form onSubmit={onSubmit}>
				<Heading tag="h2">Verify Login</Heading>
				<div>
					You will have been sent an email with a code.
					Please enter that code and press continue to finish logging in.
				</div>
				<TextField model={inputData} modelProperty="code" />
				<Checkbox model={inputData} modelProperty="rememberTwoFactor" label="Skip this step for 14 days" />
				<Button type="submit">Submit</Button>
			</form>
		</div>
	);

}

export const EmailTwoFactorConfiguration: TwoFactorConfiguration = {

	onInitialSetup: args => ({
		content: <EmailInitialSetup {...args} />,
		required: true,
	}),

	onRemove: () => ({
		content: undefined,
		required: false,
	}),

	onLogin: args => ({
		content: <EmailTwoFactorPage {...args} />,
		required: true,
	}),

};

export function configureEmail2fa(methods: TwoFactorMethods) {
	methods[EmailTokenType] = EmailTwoFactorConfiguration;
}
